import { BaseFunnelAnswers, BaseFunnelJobTitle, FunnelNavItem, LocalFunnelStep } from '../types';

/**
 * Returns `FunnelItem`s to be used for navigation.
 */
export const questionsToFunnelNavItems = <
  J extends BaseFunnelJobTitle = BaseFunnelJobTitle,
  A extends BaseFunnelAnswers<J> = BaseFunnelAnswers<J>,
  Q extends string = string,
  P extends Record<string, unknown> | undefined = undefined,
>(
  answers: A,
  currentQuestionIndex: number,
  jobTitles: J[],
  questions: LocalFunnelStep<J, A, Q, P>[],
  props: P,
) => {
  const showKeyQuestionForNavigation =
    props && typeof props === 'object' && 'showKeyQuestionForNavigation' in props
      ? (props.showKeyQuestionForNavigation as string)
      : undefined;

  const getQuestionKey = (q: LocalFunnelStep<J, A, Q, P>) => (showKeyQuestionForNavigation ? q.key : q.category);

  return questions.reduce<FunnelNavItem[]>((reduced: FunnelNavItem[], question: LocalFunnelStep<J, A, Q, P>, reduceIndex: number) => {
    if (reduced.length === 0) return [{ id: 0, isComplete: false, key: getQuestionKey(question), link: true, redirect: null }];
    const latestCategory = reduced[reduced.length - 1];
    const currentQuestionKey = getQuestionKey(question);
    if (latestCategory.key === currentQuestionKey) return reduced;
    const isLatestComplete = currentQuestionIndex >= reduceIndex;

    const redirectTarget = questions.find(redirectQuestion => {
      const isCategoryEqual =
        redirectQuestion.category === latestCategory.key ||
        (showKeyQuestionForNavigation &&
          redirectQuestion.category === showKeyQuestionForNavigation &&
          redirectQuestion.key === latestCategory.key);
      if (!redirectQuestion.condition) return isCategoryEqual;
      try {
        const isAvailable = redirectQuestion.condition(answers, jobTitles, props);
        return isCategoryEqual && isAvailable;
      } catch {
        return false;
      }
    });

    const redirectKey =
      redirectTarget?.key ??
      questions.find(q => {
        const qKey = getQuestionKey(q);
        return qKey === latestCategory.key;
      })?.key;

    return reduced.slice(0, reduced.length - 1).concat([
      {
        ...latestCategory,
        isComplete: isLatestComplete,
        redirect: isLatestComplete ? redirectKey : null,
      },
      {
        id: reduced.length,
        isComplete: false,
        key: currentQuestionKey,
        link: true,
        redirect: null,
      },
    ]);
  }, []);
};
